<template>
  <BaseDrawer
    :is_confirmed="!v$.deal.attributes.status.isValid.$invalid"
    :submit_test_id="'update-deal-status-submit'"
    :title="$t('sales.labels.update_sales_status')"
    :visible="visible_prop"
    @submit="updateDeal"
    @update:visible="$emit('update:visible', false)"
  >
    <b-field
      class="mb-4"
      :label="$t('sales.labels.status')"
    >
      <b-select
        v-model="v$.deal.attributes.status.$model"
        data-test="select-deal-status"
        :disabled="deal_prop.attributes.status === DEAL_STATUSES.SOLD"
        rounded
        @input="(status) => setDefaultDate(status)"
      >
        <option
          v-for="status in deal_statuses"
          :key="status"
          :value="status"
        >
          {{ $t(`sales.labels.statuses.${status}`) }}
        </option>
      </b-select>
    </b-field>
    <div
      v-if="deal_prop.attributes.status === DEAL_STATUSES.SOLD"
      class="my-4"
    >
      {{ $t('sales.labels.go_to_unit_part_1') }}
      <span
        class="is-underlined is-clickable"
        @click="navigateToUnit"
      >
        {{ $t('sales.labels.unit_information_page') }}
      </span>
      {{ $t('sales.labels.go_to_unit_part_2') }}
    </div>
    <div v-if="!v$.deal.attributes.status.isValid.$invalid">
      <b-field
        v-if="deal.attributes.status === DEAL_STATUSES.RESERVED"
        expanded
        :label="$t('sales.labels.expiry_date')"
        labelFor="expiry-date"
      >
        <SelectDate
          id="expiry-date"
          :date_prop="deal.attributes.reserved_expiry_date"
          @setDate="(date) => (v$.deal.attributes.reserved_expiry_date.$model = date)"
        />
      </b-field>
      <p
        v-if="v$.deal.attributes.reserved_expiry_date.required.$invalid"
        class="help is-danger"
      >
        {{ $t('sales.validation.reserved_expiry_date') }}
      </p>
      <b-field
        v-if="deal.attributes.status === DEAL_STATUSES.SOLD"
        expanded
        :label="$t('sales.validation.need_sold_date')"
        labelFor="sold-date"
      >
        <SelectDate
          id="sold-date"
          :date_prop="deal.attributes.sold_date"
          @setDate="(date) => (v$.deal.attributes.sold_date.$model = date)"
        />
      </b-field>
      <p
        v-if="v$.deal.attributes.sold_date.required.$invalid"
        class="help is-danger"
      >
        {{ $t('sales.validation.need_sold_date') }}
      </p>
      <b-field
        v-if="deal.attributes.status === DEAL_STATUSES.SOLD"
        label="Sale documents"
      >
        <div class="flex flex-col">
          <UppyFileUpload
            class="my-4"
            :max_files_size="MAX_FILE_SIZE_PREVIEW"
            :params="document_params"
            :remove_after_upload="true"
            @uploadSuccess="addAttachment"
          />
          <DocumentsUploadTable
            v-if="attachments_uploaded.length > 0"
            :attachments="attachments_uploaded"
            @removeDocument="(attachment) => deleteAttachment(attachment)"
          />
        </div>
      </b-field>
    </div>
    <div v-else>
      <div class="message is-warning">
        <div class="message-body flex items-center">
          <FontAwesomeIcon
            class="has-text-info mr-3"
            icon="circle-info"
            size="lg"
          />
          <div>
            <p>
              {{ $t('sales.validation.already_reserved_sold') }}
            </p>
            <p>
              {{ $t('sales.validation.already_reserved_sold_info') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </BaseDrawer>
</template>

<script>
import { DEAL_STATUSES, Document } from '@/app/data/model_constants';
import { mapActions, mapGetters } from 'vuex';
import SelectDate from '@/app/shared_components/selects/SelectDate.vue';
import { requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { addWeeks } from '@/app/util/date_fns';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import BaseDrawer from '@/app/shared_components/drawer/BaseDrawer.vue';
import UppyFileUpload from '@/app/shared_components/upload/UppyFileUpload.vue';
import DocumentsUploadTable from '@/app/_employees/components/documents/DocumentsUploadTable.vue';
import { MAX_FILE_SIZE_PREVIEW } from '@/app/data/document_constants';

export default {
  name: 'DealStatusModal',

  components: {
    DocumentsUploadTable,
    UppyFileUpload,
    SelectDate,
    BaseDrawer,
  },

  props: {
    deal_prop: { type: Object, required: true },
    unit: { type: Object, default: null },
    visible_prop: { type: Boolean, default: false },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      MAX_FILE_SIZE_PREVIEW,
      deal: null,
      attachments_uploaded: [],
      deal_statuses: null,
      DEAL_STATUSES,
      document_params: { category: Document.CATEGORY_INVOICE },
    };
  },

  validations() {
    return {
      deal: {
        attributes: {
          sold_date: {
            required: requiredIf((_, vm) => vm.status === DEAL_STATUSES.SOLD),
          },
          reserved_expiry_date: {
            required: requiredIf((_, vm) => vm.status === DEAL_STATUSES.RESERVED),
          },
          status: {
            isValid(value) {
              if (_.isNil(this.unit) || ![DEAL_STATUSES.SOLD, DEAL_STATUSES.RESERVED].includes(value)) return true;

              const deal_id = parseInt(this.deal.id, 10);
              const deal_for_current_unit = deal_id === this.unit.attributes.deal_reserved_id;
              const unit_has_no_deal =
                _.isNil(this.unit.attributes.deal_reserved_id) && _.isNil(this.unit.attributes.deal_sold_id);
              return unit_has_no_deal || deal_for_current_unit;
            },
          },
        },
      },
    };
  },

  computed: {
    ...mapGetters(['current_project']),
  },

  watch: {
    visible_prop: {
      handler(newValue) {
        if (newValue) {
          this.deal = _.cloneDeep(this.deal_prop);
        }
      },
      immediate: true,
    },
  },

  created() {
    this.deal = _.cloneDeep(this.deal_prop);
    const { [_.upperCase(DEAL_STATUSES.AVAILABLE)]: remove, ...rest } = DEAL_STATUSES;
    this.deal_statuses = rest;
  },

  methods: {
    ...mapActions(['UPDATE_DEAL', 'CREATE_DOCUMENTS']),

    addAttachment(file, response) {
      this.attachments_uploaded.push(response.data);
    },

    deleteAttachment(attachment) {
      const attachments_to_remove = Array.isArray(attachment) ? attachment : [attachment];
      this.attachments_uploaded = _.without(this.attachments_uploaded, ...attachments_to_remove);
    },

    async updateDeal() {
      this.v$.deal.$touch();

      if (this.v$.deal.$invalid) return;

      // don't send helper attributes
      await this.UPDATE_DEAL({
        record: { id: this.deal.id, type: this.deal.type, attributes: this.deal.attributes },
      });

      if (!_.isEmpty(this.attachments_uploaded)) {
        const attachment_id = _.map(this.attachments_uploaded, 'id');
        try {
          const attributes = {
            attachables: { [this.deal.type]: [this.deal.id] },
            attachment_id,
          };
          await this.CREATE_DOCUMENTS({ objects: { attributes } });
          this.toast_success(this.$t('toast.upload.succeeded'));
        } catch (e) {
          this.toast_fail(this.$t('toast.upload.failed'));
        } finally {
          this.$emit('submitDeal');
          this.$emit('update:visible', false);
        }
      } else {
        this.$emit('submitDeal');
        this.$emit('update:visible', false);
      }
    },

    setDefaultDate(status) {
      if (status === DEAL_STATUSES.RESERVED && !this.deal.attributes.reserved_expiry_date) {
        this.deal.attributes.reserved_expiry_date = addWeeks(new Date(), 2);
      } else if (status === DEAL_STATUSES.SOLD && !this.deal.attributes.sold_date) {
        this.deal.attributes.sold_date = new Date();
      }
    },

    navigateToUnit() {
      this.$router.push({
        name: ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS,
        params: {
          project_id: this.current_project.id,
          level_type: this.unit.type,
          level_id: this.unit.id,
        },
      });
      this.$emit('update:visible', false);
    },
  },
};
</script>
